import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

/**
 * Plain `twMerge` doesn't know about this app's custom `--text-*` font-size
 * steps (`2xs`, `3xs`, `body-sm`, `micro` — see src/index.css). Without this
 * extension it can't tell `text-micro`/`text-body-sm` apart from an arbitrary
 * custom text-COLOR utility, buckets them into the color conflict group by
 * default, and silently drops them whenever a real text-color class (e.g.
 * `text-muted-foreground`) appears later in the same `cn()` call — a badge
 * or button then renders at whatever size the base component already set,
 * with no error and nothing wrong in the source string. Registering the
 * scale here is the systemic fix, not a per-callsite class-order workaround.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['2xs', '3xs', 'body-sm', 'micro'] }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * The human-readable reason out of an axios-style error.
 *
 * Backend and machine disagree on the key: the backend envelope carries `message`, machine's
 * connect/remove routes carry `error`. Both are read, `message` first, so one helper serves every
 * screen and no component re-derives the shape.
 */
export function getApiErrorMessage(err: unknown, fallback = 'Operation failed'): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { message?: unknown; error?: unknown } } }).response;
    const message = response?.data?.message;
    if (typeof message === 'string' && message) return message;
    const error = response?.data?.error;
    if (typeof error === 'string' && error) return error;
  }
  return fallback;
}

/** JSON for display: pretty when serializable, best-effort string when not. */
export function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

/** "Cindy Barodia" → "CB" — avatar fallback initials. */
export function initialsOf(name: string | null | undefined, fallback = '?'): string {
  const source = (name ?? '').trim();
  if (!source) return fallback;
  return source
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Parses an ISO date, treating a missing timezone suffix as UTC (how the backend sends it). */
export function parseUtcDate(iso: string): Date {
  const normalized = /[Z+-]\d{2}:\d{2}$|Z$/.test(iso) ? iso : `${iso}Z`;
  return new Date(normalized);
}

/**
 * Local `YYYY-MM-DD` for a Date, e.g. a date-picker value going into a query param or payload.
 * Never `date.toISOString().slice(0, 10)` — that reads the UTC date, which is a day off from
 * the local date for any user ahead of UTC.
 */
export function toLocalDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
